import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toggleTheme } from '../lib/theme';
import { NAV_SECTIONS, visibleItems, norm } from './nav';
import { useMe } from '../features/auth/api';
import { WORK_VIEWS, DEFAULT_VIEW } from '../features/work/views';
import { t } from '../lib/i18n';

interface SearchResult {
  type: 'project' | 'prop_item' | 'material' | 'client' | 'knowledge';
  id: string;
  label: string;
  sublabel: string | null;
  projectId: string | null;
}

const TYPE_LABEL: Record<SearchResult['type'], string> = {
  project: 'Zakázka',
  prop_item: 'Výroba',
  material: 'Sklad',
  client: 'Odběratel',
  knowledge: 'Recept',
};

interface Entry {
  key: string;
  icon: string;
  label: string;
  sub?: string | null;
  group: string;
  run: () => void;
}

export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: user } = useMe();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setTerm('');
      setDebounced('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(term.trim()), 200);
    return () => window.clearTimeout(t);
  }, [term]);

  // Fulltext hledá v provozních datech — bez 'production' (koordinátor) se nevolá.
  const canSearch = !!user?.caps.includes('production');
  const { data: results } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResult[]>(`/api/search?q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.length >= 2 && canSearch,
    staleTime: 10_000,
  });

  const go = (to: string, params?: Record<string, string>, search?: Record<string, string>) => {
    onClose();
    // Dynamické cíle (statické routy i s parametry) – router API je přísně typované.
    (navigate as (opts: { to: string; params?: Record<string, string>; search?: Record<string, string> }) => void)({ to, params, search });
  };

  const entries = useMemo<Entry[]>(() => {
    const q = norm(term.trim());
    const out: Entry[] = [];

    // Akce (respektují oprávnění uživatele)
    const caps = user?.caps ?? [];
    const actions: Entry[] = [
      { key: 'a-wizard', icon: '📦', label: 'Nová zakázka', group: 'Akce', run: () => go('/zakazky/pruvodce') },
      { key: 'a-scan', icon: '📷', label: 'Skenovat QR', group: 'Akce', run: () => go('/skener') },
      { key: 'a-inventory', icon: '📋', label: 'Inventura', group: 'Akce', run: () => go('/inventura') },
      ...(caps.includes('finance')
        ? [{ key: 'a-booking', icon: '📆', label: 'Nová rezervace půjčovny', group: 'Akce', run: () => go('/pujcovna/rezervace') } as Entry]
        : []),
      { key: 'a-station', icon: '🏭', label: 'Moje stanice', group: 'Akce', run: () => go('/stanice') },
      { key: 'a-theme', icon: '🌗', label: 'Přepnout světlý/tmavý režim', group: 'Akce', run: () => { toggleTheme(); onClose(); } },
    ];
    for (const a of actions) if (!q || norm(a.label).includes(q)) out.push(a);

    // Řezy stránky Zakázky — v sidebaru je jen jedna položka, tady musí zůstat
    // všechny čtyři, jinak by se na tabuli ani gantt nedalo skočit jménem.
    for (const v of WORK_VIEWS) {
      if (!caps.includes(v.cap)) continue;
      const label = `Zakázky — ${v.label}`;
      if (q && !norm(label).includes(q)) continue;
      out.push({
        key: 'view-' + v.id,
        icon: v.icon,
        label: t(label),
        group: 'Stránky',
        run: () => go('/zakazky', undefined, v.id === DEFAULT_VIEW ? undefined : { pohled: v.id }),
      });
    }

    // Stránky (jen ty, na které má uživatel oprávnění)
    for (const area of NAV_SECTIONS.flatMap((s) => s.areas)) {
      const tabs = visibleItems(area.tabs, user?.caps ?? []);
      for (const n of tabs) {
        if (n.to === '/zakazky') continue; // pokryto řezy výše
        const label = area.tabs.length > 1 ? `${t(area.label)} — ${t(n.label)}` : t(n.label);
        if (!q || norm(label).includes(q) || norm(n.label).includes(q)) {
          out.push({ key: 'nav-' + n.to, icon: n.icon, label, group: 'Stránky', run: () => go(n.to) });
        }
      }
    }

    // Výsledky hledání (entity)
    for (const r of results ?? []) {
      out.push({
        key: r.type + r.id,
        icon: '🔎',
        label: r.label,
        sub: `${TYPE_LABEL[r.type]}${r.sublabel ? ' · ' + r.sublabel : ''}`,
        group: 'Výsledky',
        run: () => {
          if (r.type === 'project') go('/zakazky/$projectId', { projectId: r.id });
          else if (r.type === 'prop_item') go('/vyroby/$propItemId', { propItemId: r.id });
          else if (r.type === 'material') go('/sklad');
          else if (r.type === 'client') go('/klienti');
          else go('/znalosti');
        },
      });
    }
    return out;
  }, [term, results, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActive(0); }, [entries.length]);

  if (!open) return null;

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, entries.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); entries[active]?.run(); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  // Skupiny v pořadí výskytu.
  let lastGroup = '';

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center bg-black/40 p-4 pt-[12vh]" onClick={onClose}>
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onKey}
          placeholder="Skoč kamkoliv nebo hledej zakázky, výroby, sklad…"
          className="w-full border-b border-zinc-200 dark:border-zinc-800 bg-transparent px-4 py-3.5 text-sm outline-none placeholder:text-zinc-400"
        />
        <div ref={listRef} className="max-h-[55vh] overflow-auto py-1">
          {entries.length === 0 && <p className="px-4 py-6 text-center text-sm text-zinc-400">Nic nenalezeno.</p>}
          {entries.map((en, i) => {
            const head = en.group !== lastGroup ? ((lastGroup = en.group), en.group) : null;
            return (
              <div key={en.key}>
                {head && <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">{head}</div>}
                <button
                  onMouseEnter={() => setActive(i)}
                  onClick={() => en.run()}
                  className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm ${i === active ? 'bg-zinc-100 dark:bg-zinc-800' : ''}`}
                >
                  <span className="w-5 shrink-0 text-center">{en.icon}</span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{en.label}</span>
                    {en.sub && <span className="text-zinc-400"> · {en.sub}</span>}
                  </span>
                  {i === active && <span className="shrink-0 text-[10px] text-zinc-400">⏎</span>}
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 border-t border-zinc-200 dark:border-zinc-800 px-4 py-1.5 text-[10px] text-zinc-400">
          <span>↑↓ pohyb</span><span>⏎ otevřít</span><span>esc zavřít</span>
        </div>
      </div>
    </div>
  );
}
